import { FC, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { decrypt } from '@/utils/decrypt';
import { SMER } from '../api/getSmerList';
import { Link } from 'react-router-dom';
import { useUserStore } from '@/store/user';
import { useMutation } from '@tanstack/react-query';
import { setReadId, SubmitSetReadId } from '@/api/setReadId';
import i18n from '@/localisation/i18n';
import clsx from 'clsx';
import { EmotionLabel } from './EmotionLabel';

export const ItemSmer: FC<SMER & { client: string }> = data => {
  const { addSmerView, clients } = useUserStore();

  const { mutate } = useMutation({
    mutationFn: (data: SubmitSetReadId) => setReadId(data),
    onSuccess: () => {
      addSmerView(data.client!, data.id);
    },
  });

  // Зависимость — сам Set: setClientStrings пересоздаёт наборы с сервера,
  // и Set той же длины, но с другим составом, должен инвалидировать мемо.
  const smerViewed = clients[data.client!]?.smerViewed;
  const isRead = useMemo(() => {
    return smerViewed?.has(data.id);
  }, [data.id, smerViewed]);

  const setRead = () => {
    if (!isRead) {
      mutate({ user: data.client!, id: data.id });
    }
  };
  const dataKey = `${data.client}:${data.id}`;
  const [decryptedEntry, setDecryptedEntry] = useState<{
    key: string;
    text: string;
  } | null>(null);
  // Пока расшифровка в полёте (или ключ устарел) — показываем доступный
  // plaintext-fallback, а не пустоту
  const displayText =
    decryptedEntry?.key === dataKey ? decryptedEntry.text : data.text ?? null;

  useEffect(() => {
    let ignore = false;

    const handleDecryption = async () => {
      let text = data.text ?? '';
      const targetBin =
        data.textBin ||
        data.eventBin ||
        data.thoughtsBin ||
        data.solutionBin ||
        data.conclusionBin ||
        data.behaviourBin ||
        data.feelInBodyBin;

      if (targetBin) {
        try {
          text = await decrypt(targetBin, data.client);
        } catch (error) {
          Sentry.captureException(error);
        }
      }

      if (!ignore) setDecryptedEntry({ key: dataKey, text });
    };

    handleDecryption();

    return () => {
      ignore = true;
    };
  }, [
    data.text,
    data.textBin,
    data.eventBin,
    data.thoughtsBin,
    data.solutionBin,
    data.conclusionBin,
    data.behaviourBin,
    data.feelInBodyBin,
    data.client,
    dataKey,
  ]);

  return (
    <Link
      className="bg-card flex min-h-32 flex-col rounded-lg px-4 py-3"
      onClick={setRead}
      to={`${data.id}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-additional truncate pr-1 text-xs">
          {i18n.t('filled.steps', { steps: data.filled })}
        </div>
        <div
          className={clsx(
            'badge badge-outline border-additional text-additional ml-auto shrink-0',
            !isRead && 'border-orange bg-orange text-main'
          )}
        >
          {i18n.t(isRead ? 'viewed' : 'new')}
        </div>
      </div>
      <div className="mt-2 line-clamp-3 text-white">{displayText}</div>
      <div className="mt-auto overflow-hidden pt-3">
        {data.emotions?.map(emotion => (
          <EmotionLabel
            key={emotion.name}
            emotion={emotion.name}
            val={emotion.value}
          />
        ))}
      </div>
    </Link>
  );
};
